/**
 * App-wide provider composition component
 * Part of the FAANG-grade _app.tsx refactoring - Phase 3.1
 *
 * This component provides a clean, hierarchical structure for all React context providers
 * used throughout the application. It ensures proper provider ordering and error boundaries.
 */

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import ErrorBoundary from '../../components/ErrorBoundary';
import { AuthProvider } from '../../components/layout/providers/AuthProvider';
import { AddressProvider } from '../../contexts/AddressContext';
import { CartProvider } from '../../contexts/CartContext';
import { store } from '../../store';
import type { ProviderProps } from '../../types/app';

// Create MUI theme (can be moved to a separate theme file later)
const theme = createTheme();

// SSR-safe wrapper: always render children so getServerSideProps HTML is visible
// The previous version blocked SSR by only showing "Loading..." until useEffect fired
const Loading: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

/**
 * QueryClient wrapper that creates client lazily to avoid SSR issues
 * Creates the QueryClient instance directly instead of using useQueryClient()
 */
const QueryClientWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Create QueryClient instance lazily (once per client lifecycle)
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

/**
 * Base providers that are always needed
 * Redux, Auth, Cart, and Theme providers that wrap the entire application
 *
 * CartProvider is here as the single source of truth for cart context.
 * It was previously ALSO in AppLayout's ConditionalProviders, causing
 * duplicate mounting, double API calls, and 401 errors during logout.
 * The duplicate in AppLayout has been removed.
 */
export const AppProviders: React.FC<ProviderProps> = ({ children }) => {
  return (
    <ErrorBoundary>
      <Loading>
        <ReduxProvider store={store}>
          <AuthProvider>
            <CartProvider>
              <AddressProvider>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <QueryClientWrapper>{children || null}</QueryClientWrapper>
              </ThemeProvider>
              </AddressProvider>
            </CartProvider>
          </AuthProvider>
        </ReduxProvider>
      </Loading>
    </ErrorBoundary>
  );
};

/**
 * Test-friendly version of AppProviders with mock store
 * Used in testing environments where a real Redux store isn't needed
 */
export const TestAppProviders: React.FC<ProviderProps> = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children || null}
    </ThemeProvider>
  );
};
